import { devLog } from './devLog';
import { noteEvent } from './diagnostics';
import { isLocalHost } from './isLocalHost';

/**
 * Capture health: which stage of the recording pipeline is actually moving.
 *
 * Built for one bug and kept as the show-night trace. The recorded clip froze
 * at the trigger on the kiosk while the live view stayed at 60fps, and nothing
 * in the flow could say WHERE the picture stopped. The pipeline has four stages
 * that can each stall on their own:
 *
 *   remote <video> ──► AR canvas ──► compositor canvas ──► captureStream ──► MediaRecorder
 *   (WebRTC decode)    (rAF paint)    (rAF tick)            (GPU readback)     (1s chunks)
 *
 * Each stage increments a counter here; a 500ms sampler turns them into rates
 * and logs one line. The decisive signal is the LAST one: a 1s chunk that
 * shrinks to a few KB while the compositor is still ticking means the encoder
 * is being handed a static picture — the capture stage is starving, not the
 * source and not the drawing. Everything else is there to rule out the other
 * stages in the same run.
 *
 * Counters are plain integer increments and cost nothing on the hot path. The
 * sampler allocates only in its own tick, and only runs between the capture
 * screen going live and the recording being stopped.
 *
 * `window.__incomerDebug` exposes a snapshot and a trigger, gated the same way
 * devLog is (localhost or the kiosk shell), so a DevTools-driven run can key on
 * app state instead of a fixed sleep — the lesson of docs/KIOSK_PERFORMANCE.md.
 */

export interface RemoteVideoStats {
  framesDecoded: number;
  framesReceived: number;
  framesDropped: number;
  freezeCount: number;
  totalFreezesDuration: number;
  decoderImplementation: string;
  /**
   * `getVideoPlaybackQuality().totalVideoFrames` on the receiving <video>.
   * Synchronous and independent of requestVideoFrameCallback, which may never
   * fire for an element that is not in the document.
   */
  videoTotalFrames: number;
  /** How many times the module has painted the <video> into its canvas. */
  paints: number;
}

interface Chunk {
  /** Wall clock the chunk landed. */
  at: number;
  bytes: number;
}

interface Sample {
  at: number;
  phase: string;
  rafFps: number;
  remoteFps: number;
  videoFps: number | null;
  paintFps: number | null;
  compositorFps: number;
  captureFps: number | null;
  chunkKBps: number;
  decodedFps: number | null;
}

const SAMPLE_MS = 500;
/**
 * A 1s chunk below this holds audio and a picture that did not change. Opus at
 * the default rate is ~6KB/s and a static VP8 frame a few KB more; a moving
 * 720x1280 picture at 4Mbps is hundreds of KB.
 */
const STARVED_CHUNK_BYTES = 25_000;
const MAX_HISTORY = 120;

const counters = {
  rafFrames: 0,
  remoteFrames: 0,
  compositorTicks: 0,
  chunkBytes: 0,
};

const chunks: Chunk[] = [];
const samples: Sample[] = [];

let captureTrack: MediaStreamTrack | null = null;
let statsProvider: (() => Promise<RemoteVideoStats | null>) | null = null;
let triggerHook: (() => void) | null = null;
let phaseOf: () => string = () => '?';

let running = false;
let startedAt = 0;
let triggerAt = 0;
let timer: number | null = null;
let rafId = 0;
let lastRtc: RemoteVideoStats | null = null;
let last: {
  at: number;
  raf: number;
  remote: number;
  comp: number;
  cap: number | null;
  decoded: number | null;
  video: number | null;
  paints: number | null;
} | null = null;
let lastLine = '';

/**
 * A hidden <video> playing the capture track, so its frames can be counted
 * when `MediaStreamTrack.stats` is missing — which it is on the unit's WebView
 * 149. The sink is a second consumer of the same track; frames are produced
 * once per canvas paint and fanned out, so it adds no readbacks. Kiosk only.
 */
let captureSink: HTMLVideoElement | null = null;

function attachCaptureSink(track: MediaStreamTrack): void {
  if (!inKioskShell() || typeof document === 'undefined') return;
  if (!captureSink) {
    captureSink = document.createElement('video');
    captureSink.muted = true;
    captureSink.playsInline = true;
    captureSink.setAttribute('aria-hidden', 'true');
    Object.assign(captureSink.style, {
      position: 'fixed',
      width: '2px',
      height: '2px',
      opacity: '0',
      pointerEvents: 'none',
      zIndex: '-1',
    });
    document.body.appendChild(captureSink);
  }
  captureSink.srcObject = new MediaStream([track]);
  void captureSink.play().catch(() => undefined);
}

function detachCaptureSink(): void {
  if (!captureSink) return;
  captureSink.srcObject = null;
  captureSink.remove();
  captureSink = null;
}

/** `MediaStreamTrack.stats` when present, else the sink's presented-frame count. */
function captureDelivered(): number | null {
  const t = captureTrack as (MediaStreamTrack & { stats?: { deliveredFrames?: number } }) | null;
  const n = t?.stats?.deliveredFrames;
  if (typeof n === 'number') return n;
  if (captureSink && typeof captureSink.getVideoPlaybackQuality === 'function') {
    return captureSink.getVideoPlaybackQuality().totalVideoFrames;
  }
  return null;
}

function inKioskShell(): boolean {
  try {
    const s = (globalThis as { __KIOSK__?: { shell?: string } }).__KIOSK__;
    return s?.shell === 'android';
  } catch {
    return false;
  }
}

function fps(delta: number, ms: number): number {
  return ms > 0 ? Math.round((delta / ms) * 1000) : 0;
}

function countRaf(): void {
  if (!running) return;
  counters.rafFrames += 1;
  rafId = requestAnimationFrame(countRaf);
}

function bytesInLastSecond(now: number): number {
  let total = 0;
  for (let i = chunks.length - 1; i >= 0; i -= 1) {
    const c = chunks[i];
    if (!c || now - c.at > 1000) break;
    total += c.bytes;
  }
  return total;
}

function tick(): void {
  if (!running) return;
  const now = Date.now();
  const cap = captureDelivered();
  const decoded = lastRtc?.framesDecoded ?? null;
  const video = lastRtc?.videoTotalFrames ?? null;
  const paints = lastRtc?.paints ?? null;
  const prev = last;
  last = {
    at: now,
    raf: counters.rafFrames,
    remote: counters.remoteFrames,
    comp: counters.compositorTicks,
    cap,
    decoded,
    video,
    paints,
  };

  if (statsProvider) {
    void statsProvider().then((s) => {
      if (s) lastRtc = s;
    }, () => undefined);
  }
  if (!prev) return;

  const ms = now - prev.at;
  const delta = (a: number | null, b: number | null): number | null =>
    a !== null && b !== null ? fps(a - b, ms) : null;
  const sample: Sample = {
    at: now,
    phase: phaseOf(),
    rafFps: fps(last.raf - prev.raf, ms),
    remoteFps: fps(last.remote - prev.remote, ms),
    videoFps: delta(video, prev.video),
    paintFps: delta(paints, prev.paints),
    compositorFps: fps(last.comp - prev.comp, ms),
    captureFps: delta(cap, prev.cap),
    chunkKBps: Math.round(bytesInLastSecond(now) / 1024),
    decodedFps: delta(decoded, prev.decoded),
  };
  samples.push(sample);
  if (samples.length > MAX_HISTORY) samples.shift();

  const since = `+${now - startedAt}ms`;
  const trig = triggerAt ? ` T+${now - triggerAt}ms` : '';
  const rtc = lastRtc
    ? ` rtc dec=${sample.decodedFps ?? '?'} drop=${lastRtc.framesDropped} frz=${lastRtc.freezeCount}`
    : '';
  // remote = frames presented (rVFC); vid = totalVideoFrames; paint = module
  // draws; comp = compositor ticks; cap = capture-track frames; chunk = encoder
  // output. Read left to right: the first column that stops is the stage that
  // stopped.
  lastLine =
    `[health] ${since}${trig} ${sample.phase} raf=${sample.rafFps} remote=${sample.remoteFps} ` +
    `vid=${sample.videoFps ?? 'n/a'} paint=${sample.paintFps ?? 'n/a'} comp=${sample.compositorFps} ` +
    `cap=${sample.captureFps ?? 'n/a'} chunk=${sample.chunkKBps}KB/s${rtc}`;
  devLog(lastLine);
}

function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

/**
 * One line for the admin log. Written to read at a glance in a dark lobby:
 * either the picture kept moving, or it stopped and here is which stage stopped.
 */
function summary(): string {
  const from = triggerAt || startedAt;
  const after = chunks.filter((c) => c.at >= from);
  const window = samples.filter((s) => s.at >= from);
  const comp = mean(window.map((s) => s.compositorFps));
  const present = (values: (number | null)[]): string => {
    const v = values.filter((n): n is number => n !== null);
    return v.length ? `${mean(v)}fps` : 'n/a';
  };
  const stream = present(window.map((s) => s.videoFps ?? (s.remoteFps || null)));
  const cap = present(window.map((s) => s.captureFps));
  const sizes = after.map((c) => `${Math.round(c.bytes / 1024)}K`).join(',');

  if (after.length === 0) return 'capture health: no recorder chunks after the trigger';

  // The last chunk is the flush at stop() and covers whatever fraction of a
  // second was left — a few KB is normal there, not starvation.
  const fullChunks = after.length > 1 ? after.slice(0, -1) : after;
  const starved = fullChunks.find((c) => c.bytes < STARVED_CHUNK_BYTES);
  if (starved) {
    const at = Math.max(0, starved.at - 1000 - from);
    return (
      `capture health: VIDEO STARVED from T+${at}ms — chunks ${sizes} while compositor ${comp}fps, ` +
      `stream ${stream}, capture track ${cap}` +
      (lastRtc ? `, decoder ${lastRtc.decoderImplementation}` : '')
    );
  }
  return `capture health: ok — chunks ${sizes}, compositor ${comp}fps, stream ${stream}, capture track ${cap}`;
}

export const captureHealth = {
  /** A frame was presented by the remote <video>. */
  remoteFrame(): void {
    counters.remoteFrames += 1;
  },
  /** The logo compositor drew a frame. */
  compositorTick(): void {
    counters.compositorTicks += 1;
  },
  /** MediaRecorder delivered a timeslice. */
  recorderChunk(bytes: number): void {
    counters.chunkBytes += bytes;
    chunks.push({ at: Date.now(), bytes });
    if (chunks.length > MAX_HISTORY) chunks.shift();
  },
  /** The canvas capture track the recorder reads from (latest wins). */
  setCaptureTrack(track: MediaStreamTrack | null): void {
    captureTrack = track;
    if (!track) {
      detachCaptureSink();
      return;
    }
    // `stats` is present but NULL for a canvas track on WebView 149, so the
    // check is for a usable counter, not for the property.
    const stats = (track as MediaStreamTrack & { stats?: { deliveredFrames?: number } | null }).stats;
    if (typeof stats?.deliveredFrames !== 'number') attachCaptureSink(track);
  },
  /** How to read the incoming stream's decoder stats, when there is one. */
  setStatsProvider(provider: (() => Promise<RemoteVideoStats | null>) | null): void {
    statsProvider = provider;
  },
  /** What `__incomerDebug.trigger()` should do — the capture screen's manual path. */
  setTrigger(hook: (() => void) | null): void {
    triggerHook = hook;
  },
  trigger(): boolean {
    if (!triggerHook) return false;
    triggerHook();
    return true;
  },
  /** The transformation began; every line from here is stamped relative to it. */
  markTrigger(): void {
    triggerAt = Date.now();
  },

  /** Begin sampling. Idempotent; called when the capture screen goes live. */
  start(phase: () => string): void {
    if (running) return;
    running = true;
    phaseOf = phase;
    startedAt = Date.now();
    triggerAt = 0;
    chunks.length = 0;
    samples.length = 0;
    last = null;
    lastRtc = null;
    lastLine = '';
    rafId = requestAnimationFrame(countRaf);
    timer = window.setInterval(tick, SAMPLE_MS);
    devLog('[health] sampling');
  },

  /** Stop sampling and write the one-line verdict. Safe to call when idle. */
  stop(reason: string): void {
    if (!running) return;
    running = false;
    if (timer !== null) clearInterval(timer);
    timer = null;
    cancelAnimationFrame(rafId);
    rafId = 0;
    detachCaptureSink();
    devLog(`[health] stopped (${reason})`);
    if (chunks.length > 0 || triggerAt) noteEvent(summary());
  },

  snapshot(): Record<string, unknown> {
    const now = Date.now();
    return {
      running,
      phase: phaseOf(),
      sinceStartMs: running ? now - startedAt : null,
      sinceTriggerMs: triggerAt ? now - triggerAt : null,
      counters: { ...counters },
      captureFramesDelivered: captureDelivered(),
      rtc: lastRtc,
      lastLine,
      recent: samples.slice(-6),
      chunks: chunks.slice(-8),
    };
  },
};

/** Same gate as devLog: the unit and a developer's machine, nowhere public. */
function debugSurfaceEnabled(): boolean {
  return inKioskShell() || isLocalHost();
}

if (typeof window !== 'undefined' && debugSurfaceEnabled()) {
  (window as unknown as { __incomerDebug?: unknown }).__incomerDebug = {
    snapshot: () => captureHealth.snapshot(),
    trigger: () => captureHealth.trigger(),
  };
}
